import { Fragment, useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Container, Row, Col, Image, Button, Form, Stack, Modal } from "react-bootstrap";
import useAxios from "axios-hooks";
import BrandContext from "./BrandContext";
import { useSignIn, useSignOut, useIsAuthenticated } from 'react-auth-kit'
import { format } from 'date-fns'

export default function Login(props) {
    const isAuthenticated = useIsAuthenticated();
    const params = useParams();
    const signIn = useSignIn()
    const signOut = useSignOut()
    const nav = useNavigate();
    const [email, setEmail] = useState('');
    const [codeRequested, setCodeRequested] = useState(false)
    const [code, setCode] = useState('')
    const [msg, setMsg] = useState({})
    const [show, setShow] = useState(false);
    const handleClose = () => setShow(false);
    const handleShow = (msg) => { setMsg(msg); setShow(true) };

    // Logout if user is for a different PR
    useEffect(() => {
        if (isAuthenticated()) {
            signOut();
        }
    }, []);

    const [{ data: currentPR, loading: loadingPR, error: prError }, refetchPR] = useAxios({
        method: 'get',
        url: '/pr/get',
        params: {
            id: params.prId
        },
    });

    const [{ loading: gettingCode }, executeGetCode] = useAxios(
        {
            url: '/pr/get-code',
            method: 'POST',
            baseURL: process.env.REACT_APP_AUTH_URL
        },
        { manual: true }
    )
    const [{ loading: validatingCode }, executeValidateCode] = useAxios(
        {
            url: '/pr/validate-code',
            method: 'POST',
            baseURL: process.env.REACT_APP_AUTH_URL
        },
        { manual: true }
    )

    function validateCode() {
        executeValidateCode({
            data: {
                email: email,
                accountId: params.accountId,
                brandId: params.brandId,
                prId: params.prId,
                verificationCode: code
            }
        })
            .then((res) => {
                if (signIn(
                    {
                        token: res.data.jwt,
                        expiresIn: 60 * 23,
                        tokenType: "Bearer",
                        authState: {
                            email: email,
                            accountId: params.accountId,
                            brandId: params.brandId,
                            prId: params.prId,
                        }
                    }
                )) {
                    nav('../edit')
                } else {
                    //Throw error
                }
            })
            .catch((e) => {
                const status = e?.response?.status;
                if (status === 401) handleShow({
                    title: 'Invalid Verification Code',
                    body: 'Sorry, the verification code you entered is not correct. Please try again or request a new code.'
                })
                if (status === 403) handleShow({
                    title: 'Email Verfication Error',
                    body: 'Sorry, only the original submitter and press release contact are authorized to edit this press release.'
                })
            })
            .finally(() => {

            })
    }

    function getCode() {
        executeGetCode({
            data: {
                email: email,
                accountId: params.accountId,
                brandId: params.brandId,
                prId: params.prId,
            }
        })
            .then((r) => {
                handleShow({
                    title: 'Email Verification',
                    body: `A verification code was sent to ${email}. Copy the code, click out of this popup and enter the verification code in the field below.`
                })
                setCodeRequested(true);
            })
            .catch((e) => {
                const status = e?.response?.status;
                if (status === 403) handleShow({
                    title: 'Email Verification Error',
                    body: 'Sorry, only the original submitter and press release contact are authorized to edit this press release.'
                })
            })
            .finally(() => {

            })
    }

    return (
        <BrandContext.Consumer>
            {theme =>
                <Fragment>
                    <Container fluid="md" style={{ paddingTop: "50px", paddingBottom: "50px" }}>
                        <Row>
                            <Col sm={{ span: true, order: 'first' }} xs={{ span: 12, order: 'last' }} style={{ padding: "15px" }}>
                                <p className="fw-bold" style={{ fontSize: "24px", lineHeight: "26px" }}>Edit My Press Release</p>
                            </Col>
                            <Col sm={{ span: 'auto', order: 'last' }} xs={{ span: 12, order: 'first' }} style={{ padding: "15px", display: "grid", alignContent: "start", justifyContent: "start" }}>
                                <Image style={{ height: "50px" }} fluid src={theme?.marketing?.logo}></Image>
                            </Col>
                        </Row>
                        {loadingPR && <div>Loading press release...</div>}
                        {currentPR &&
                            <Fragment>
                                <Row>
                                    <Col sm={{ span: 'auto', order: 'first' }}><Image style={{ height: "64px" }} fluid src={currentPR?.company?.logo}></Image></Col>
                                    <Col sm={{ span: true, order: 'last' }}>
                                        {currentPR &&
                                            <Fragment>
                                                <p style={{ fontSize: "18px", marginBottom: "0.5rem" }}>{currentPR?.prTitle}</p>
                                                <p style={{}}>{format(new Date(currentPR?.prPostDate), 'PP')}</p>
                                            </Fragment>
                                        }
                                    </Col>
                                </Row>
                                <Row>
                                    <Col md={{ span: 6, order: 3 }} style={{ padding: "15px" }} xs={{ span: 12, order: 2 }}>
                                        <Form noValidate>
                                            <Form.Group className="mb-3" controlId="customer.email">
                                                <Form.Label>Unlock Edit Access</Form.Label>
                                                <Form.Control autoFocus required type="email"
                                                    placeholder="Enter your email to get started"
                                                    onChange={(e) => setEmail(e.target.value)}
                                                    readOnly={gettingCode || codeRequested} />
                                            </Form.Group>
                                            {codeRequested &&
                                                <Form.Group className="mb-3" controlId="customer.name">
                                                    <Form.Label>Verification Code</Form.Label>
                                                    <Form.Control required type="text"
                                                        placeholder="Enter verification code"
                                                        onChange={event => setCode(event.target.value)}
                                                    />
                                                </Form.Group>
                                            }
                                        </Form>
                                    </Col>
                                </Row>
                                <Stack direction="horizontal" gap={3}>
                                    {!codeRequested &&
                                        <Button onClick={() => getCode()} disabled={!email || gettingCode || validatingCode} variant="swiftrev">Next</Button>
                                    }
                                    {gettingCode && <div>Requesting verification code...</div>}
                                    {codeRequested &&
                                        <Fragment>
                                            <Button onClick={() => validateCode()} disabled={!email || !code || gettingCode || validatingCode} variant="swiftrev">Next</Button>
                                            <Button onClick={() => setCodeRequested(false)} variant="link">Request a new code</Button>
                                        </Fragment>
                                    }
                                </Stack>
                            </Fragment>
                        }
                    </Container>
                    <Modal show={show} onHide={handleClose} size="md" centered>
                        <Modal.Header closeButton>
                            <Modal.Title>{msg.title}</Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            {msg.body}
                        </Modal.Body>
                    </Modal>
                </Fragment>
            }
        </BrandContext.Consumer>
    );
}